import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import type { WorkBook } from "xlsx";
import { RendererProps } from "../FilePreviewModal";
import FallbackRenderer from "./FallbackRenderer";
import { formatBytes } from "../previewTypes";
import SheetView from "../sheet/SheetView";
import { RawSheet } from "../sheet/sheetTypes";
import { SheetLoader } from "../sheet/useSheetView";

/**
 * Reads the workbook and hands its sheets to SheetView. Sheets are converted
 * one at a time, on demand: a ten-sheet workbook used to convert all ten before
 * drawing the first one.
 */

/**
 * Unlike a csv, a workbook cannot be read in part - so the size guard asks
 * instead of truncating, and says how big the file is before it costs anybody
 * a frozen tab.
 */
const HEAVY_FILE_BYTES = 25 * 1024 * 1024;

const XlsxRenderer: React.FC<RendererProps> = ({
  blob,
  fileName,
  fileSize,
  sourceKey,
  stackIndex,
  headerSlot,
  onDownloadOriginal,
  claimHeaderDownload,
  getPopupContainer,
}) => {
  const size = fileSize ?? blob?.size ?? 0;
  const [confirmed, setConfirmed] = useState(size <= HEAVY_FILE_BYTES);
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const xlsxRef = useRef<typeof import("xlsx") | null>(null);

  useEffect(() => {
    if (!blob || !confirmed) return;
    const fileBlob = blob;
    let cancelled = false;

    const run = async () => {
      try {
        const arrayBuffer = await fileBlob.arrayBuffer();
        if (cancelled) return;
        const xlsxModule = await import("xlsx");
        if (cancelled) return;
        xlsxRef.current = xlsxModule;
        const parsed = xlsxModule.read(arrayBuffer, { type: "array" });
        if (cancelled) return;
        setWorkbook(parsed);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to render spreadsheet",
          );
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [blob, confirmed]);

  const convertSheet = useCallback(
    (name: string): RawSheet => {
      const xlsxModule = xlsxRef.current;
      if (!workbook || !xlsxModule) {
        return { id: name, name, matrix: [], columnCount: 0 };
      }
      const worksheet = workbook.Sheets[name];
      const aoa = xlsxModule.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false,
      }) as string[][];

      let columnCount = 0;
      for (let i = 0; i < aoa.length; i++) {
        const row = aoa[i] || [];
        if (row.length > columnCount) columnCount = row.length;
      }

      const matrix: string[][] = new Array(aoa.length);
      for (let i = 0; i < aoa.length; i++) {
        const row = aoa[i] || [];
        const normalized: string[] = new Array(columnCount);
        for (let c = 0; c < columnCount; c++) {
          const cell = row[c];
          normalized[c] = cell == null ? "" : String(cell);
        }
        matrix[i] = normalized;
      }
      return { id: name, name, matrix, columnCount };
    },
    [workbook],
  );

  const loaders = useMemo<SheetLoader[]>(() => {
    if (!workbook) return [];
    return workbook.SheetNames.map((name, index) => ({
      id: name || `sheet${index}`,
      name: name || `Sheet ${index + 1}`,
      load: () => convertSheet(name),
    }));
  }, [workbook, convertSheet]);

  if (!confirmed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This workbook is {formatBytes(size)}. Opening it in the browser reads
          the whole file into memory and may take a while.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setConfirmed(true)}
          >
            Open anyway
          </Button>
          {onDownloadOriginal && (
            <Button
              variant="outlined"
              size="small"
              onClick={onDownloadOriginal}
            >
              Download instead
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <FallbackRenderer
        blob={blob}
        blobUrl=""
        fileName={fileName}
        onDownload={onDownloadOriginal ?? (() => undefined)}
        reason={`This spreadsheet could not be read in the browser (${error}). Download it to open in Excel.`}
      />
    );
  }

  if (!workbook) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 1,
          p: 4,
        }}
      >
        <CircularProgress size={28} />
        {size > 5 * 1024 * 1024 && (
          <Typography variant="body2" color="text.secondary">
            Reading {formatBytes(size)}...
          </Typography>
        )}
      </Box>
    );
  }

  if (loaders.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No sheets found in the workbook.</Alert>
      </Box>
    );
  }

  return (
    <SheetView
      loaders={loaders}
      fileName={fileName}
      fileSizeLabel={formatBytes(size)}
      sourceKey={sourceKey ?? fileName}
      stackIndex={stackIndex ?? 0}
      headerSlot={headerSlot}
      onDownloadOriginal={onDownloadOriginal}
      claimHeaderDownload={claimHeaderDownload}
      getPopupContainer={getPopupContainer}
    />
  );
};

export default XlsxRenderer;
